/* eslint-disable @typescript-eslint/restrict-template-expressions */
import axios, {AxiosResponse} from 'axios';
import { BrowserAuthError, InteractionRequiredAuthError, SilentRequest, AuthError} from "@azure/msal-browser";
import {loginRequest} from "../authConfig";
import {msalInstance} from "../index";
import cookies from 'js-cookie';
import { REACT_APP_BASE_URL } from '../urlConfig';
import logger from '../utils/logger';
import { LOGIN_ACCESS_TOKEN_COOKIE, LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE } from '../utils/loginAccessTokenCookie';

const baseURL = REACT_APP_BASE_URL;
const msGraphURL = 'https://graph.microsoft.com/v1.0';

const http =
    baseURL != null
        ? axios.create({
            baseURL: `${baseURL}/`,
            timeout: 300000,
        })
        : axios.create({
            timeout: 300000,
        });

const httpPrivate = axios.create({
    baseURL: baseURL != null ? `${baseURL}/` : undefined,
    timeout: 300000
});

const httpMsGraph =
    baseURL != null
        ? axios.create({
            baseURL: `${msGraphURL}/`,
            timeout: 300000,
        })
        : axios.create({
            timeout: 300000,
        });

httpPrivate.interceptors.request.use(async (config) => {
    logger.api.debug('Private API request:', config.method?.toUpperCase(), config.url);
    config.withCredentials = true;
    return config;
});

httpPrivate.interceptors.request.use(
    async (config) => {
        const accessToken = await getAccessToken((config as any).preventRedirect);
        if (accessToken !== '') {
            // Ensure cookie is available on all paths so Apache auth-check can validate protected JS requests
            cookies.set(LOGIN_ACCESS_TOKEN_COOKIE, accessToken, {path: '/', secure: process.env.NODE_ENV === 'production'});
            cookies.remove(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, {path: '/'});
        } else {
            cookies.remove(LOGIN_ACCESS_TOKEN_COOKIE, {path: '/'});
            cookies.remove(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, {path: '/'});
        }

        if (config.headers != null) {
            const msGraphAccessToken = await getAccessToken(true, {
                scopes: [`User.Read`],
            });
            if (accessToken !== '') {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
            if (msGraphAccessToken !== '') {
                config.headers.Msgraphtoken = msGraphAccessToken;
            }
            config.headers['Content-Type'] =
                config.headers['Content-Type'] ?? 'application/json';
        }
        return config;
    },
    (error) => {
        void Promise.reject(error);
    }
)

httpMsGraph.interceptors.request.use(
    async config => {
        if (config.headers != null) {
            const accessToken = await getAccessToken(true, {
                scopes: [`User.Read`],
            });
            if (accessToken !== '') {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
        }
        return config;
    }
)

httpPrivate.interceptors.response.use(
    (response) => {
        logger.api.debug('Private API response:', response.config.method?.toUpperCase(), response.config.url, 'Status:', response.status);
        return response;
    },
    async (error) => {
        // eslint-disable-next-line no-debugger,@typescript-eslint/no-non-null-assertion
        const originalRequest = error.config!;
        logger.api.error('Private API error:', originalRequest.method?.toUpperCase(), originalRequest.url, 'Status:', error.response?.status);
        logger.api.warn('MSAL accounts:', msalInstance.getAllAccounts());
        if (error.response?.status === 401 && !(originalRequest._retry === true)) {
            logger.api.debug('Retrying with new access token');
            const accessToken = await getAccessToken(originalRequest.preventRedirect);
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${accessToken}`
            axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            return await axios(originalRequest);
        }
        return await Promise.reject(error);
    }
)

export const getAccessToken = async (
  _preventRedirect: (boolean | undefined) = false, // this is no longer used, as API without properly configured token is unesless, we have to acquire new token by redirect to microsoft login service
  loginRequestIn: SilentRequest = loginRequest,
  loginRedirectRequest: SilentRequest = loginRequest
): Promise<string> => {
    try {
        const response = await msalInstance
            .acquireTokenSilent({
                ...loginRequestIn,
                account: (msalInstance.getAllAccounts())[0]
            });
        return response.accessToken;
    } catch (error) {
        if ((error instanceof InteractionRequiredAuthError ||
          (error instanceof BrowserAuthError && error.errorCode === 'monitor_window_timeout') ||
          (error instanceof AuthError && error.errorCode === 'invalid_grant') ||
           (msalInstance.getAllAccounts() == null || msalInstance.getAllAccounts().length === 0))) {
            try {
                logger.auth.info('REDIRECTING TO AZURE FROM AXIOS', error)
                await msalInstance
                    .acquireTokenRedirect(loginRedirectRequest);

            } catch (popuperror) {
                logger.auth.warn('Popup error:', popuperror);
            }
        }
        logger.auth.warn('Uncaught error:', error);
    }
    return '';
}


http.interceptors.request.use(async (config) => {
    logger.api.debug('Public API request:', config.method?.toUpperCase(), config.url);
    config.withCredentials = true;
    return config;
});


// Add a response interceptor
// TO DO HANDLE UNAUTHORIZED INTERCEPTOR
http.interceptors.response.use(
    (response) => {
        logger.api.debug('Public API response:', response.config.method?.toUpperCase(), response.config.url, 'Status:', response.status);
        return response;
    },
    async function (error) {
        logger.api.error('Public API error:', error.config?.method?.toUpperCase(), error.config?.url, 'Status:', error.response?.status);
        logger.api.warn('Error details:', error);
        if (error.response != null && (error.response.status === 420 || error.response.status === 302)) {
            if (window.location.pathname !== '/') {
                const win: Window = window;
                win.location = `${baseURL}/expired`;
                return;
            }
        }
        return await Promise.reject(error);
    }
);

async function get(
    url: string,
    headers = {},
    params = {}
): Promise<AxiosResponse<any, any>> {
    return await http.get(url, {
        ...params,
        headers: {
            ...headers,
        },
    });
}

async function post(
    url: string,
    data: any,
    headers = {},
    params = {}
): Promise<AxiosResponse<any, any>> {
    return await http.post(url, data, {
        ...params,
        headers,
    });
}

async function put(
    url: string,
    data: any,
    headers = {}
): Promise<AxiosResponse<any, any>> {
    return await http.put(url, data, {headers});
}

async function deleteRequest(
    url: string,
    headers = {},
    params = {}
): Promise<AxiosResponse<any, any>> {
    return await http.delete(url, {
        ...params,
        headers,
    });
}

export default {
    http,
    httpPrivate,
    httpMsGraph,
    baseURL,
    get,
    post,
    put,
    deleteRequest,
};
